import type {AttachmentCollectionFragment, ColumnFragment, ValueInstanceFragment} from "app/gql/graphqlSchema";
import {IonButton, IonCardContent, IonCardHeader, IonCardSubtitle, IonCardTitle} from "@ionic/react";
import {isNil, isNotBlank} from "app/utils/stdlib";
import {FlexColumn, FlexColumnCenter} from "app/employee/flexUtils";
import type {ExtractType} from "app/utils/types";
import {useControlCardInstanceContext} from "app/employee/controlCard/ControlCardInstanceContext";
import {useHasControlCardValueError} from "app/employee/controlCard/hook/useHasControlCardValueError";
import {useSaveControlCardValue} from "app/employee/controlCard/hook/useSaveControlCardValue";
import {DisplayValueInstanceError} from "app/employee/controlCard/input/DisplayValueInstanceError";
import {InputIonCard} from "app/employee/controlCard/input/InputIonCard";
import {AttachmentCollection} from "app/employee/att/AttachmentCollection";
import {AddAttachment} from "app/employee/att/AddAttachment";

interface Props {
  column: ExtractType<ColumnFragment, "ControlCardAttachmentCollectionColumn">;
  valueInstance: ValueInstanceFragment;
  disabled?: boolean;
}

export const InputAttachmentCollection = ({column, valueInstance, disabled}: Props) => {
  const {isComplete, canEdit} = useControlCardInstanceContext();
  const hasError = useHasControlCardValueError(valueInstance);

  const {revalidateValue} = useSaveControlCardValue(valueInstance);

  const localDisabled = isComplete || !!disabled || !canEdit;

  const persistedValue = valueInstance.value;

  let value: AttachmentCollectionFragment | null = null;
  if (persistedValue.__typename === "ControlCardExistingAttachmentCollectionOwningRefValue") {
    value = persistedValue.attachmentCollection;
  }

  const capture = column.openCameraDirectly === true ? "environment" : undefined;

  return (
    <InputIonCard $hasError={hasError} data-scroll-to-value-instance-id={valueInstance.id}>
      <IonCardHeader>
        <IonCardTitle>{column.title}</IonCardTitle>
        {isNotBlank(column.description) && <IonCardSubtitle>{column.description}</IonCardSubtitle>}
      </IonCardHeader>
      <IonCardContent>
        <FlexColumn>
          {!isNil(value) &&
            (value?.attachments.length > 0 ? (
              <AttachmentCollection
                asDisabled
                attCollection={value}
                canDelete={!localDisabled}
                canUpload={!localDisabled}
                capture={capture}
                name={column.title}
                onDeleteCompleted={revalidateValue}
                onUploadCompleted={revalidateValue}
              />
            ) : (
              <FlexColumnCenter>
                <AddAttachment
                  as={IonButton}
                  attCollection={value}
                  capture={capture}
                  disabled={localDisabled}
                  onUploadCompleted={revalidateValue}
                  size="large"
                />
              </FlexColumnCenter>
            ))}
          <FlexColumnCenter>
            <DisplayValueInstanceError column={column} disabled={localDisabled} valueInstance={valueInstance} />
          </FlexColumnCenter>
        </FlexColumn>
      </IonCardContent>
    </InputIonCard>
  );
};
